import ArticleIcon from "@mui/icons-material/Article";
import AutoStoriesOutlinedIcon from "@mui/icons-material/AutoStoriesOutlined";
import BiotechIcon from "@mui/icons-material/Biotech";
import CodeIcon from "@mui/icons-material/Code";
import CoPresentIcon from "@mui/icons-material/CoPresent";
import InsightsIcon from "@mui/icons-material/Insights";
import LinkIcon from "@mui/icons-material/Link";
import PictureAsPdfIcon from "@mui/icons-material/PictureAsPdf";
import PodcastsIcon from "@mui/icons-material/Podcasts";
import PsychologyIcon from "@mui/icons-material/Psychology";
import SlideshowIcon from "@mui/icons-material/Slideshow";
import SmartDisplayOutlinedIcon from "@mui/icons-material/SmartDisplayOutlined";
import SubtitlesOutlinedIcon from "@mui/icons-material/SubtitlesOutlined";
import TouchAppIcon from "@mui/icons-material/TouchApp";
import { ReactElement } from "react";
import { DeliveryMethodSettingValue } from "services/KnowfullyAdminClient";
import {
  DeliveryMethodId,
  DeliveryMethodSettingCodeConstants,
} from "types/common-types";

export enum DeliveryMethodPreviewBehavior {
  Dialog = "Dialog",
  NewTab = "NewTab",
  None = "None",
}

const iconsByDeliveryMethodId: Record<string, ReactElement> = {
  [DeliveryMethodId.Inxpo.toLowerCase()]: <CoPresentIcon />,
  [DeliveryMethodId.Zoom.toLowerCase()]: <CoPresentIcon />,
  [DeliveryMethodId.ThreePlayMedia.toLowerCase()]: <SubtitlesOutlinedIcon />,
  [DeliveryMethodId.Video.toLowerCase()]: <SmartDisplayOutlinedIcon />,
  [DeliveryMethodId.Vimeo.toLowerCase()]: <SmartDisplayOutlinedIcon />,
  [DeliveryMethodId.Blubrry.toLowerCase()]: <PodcastsIcon />,
  [DeliveryMethodId.ArcheMedX.toLowerCase()]: <TouchAppIcon />,
  [DeliveryMethodId.ArticulateRise.toLowerCase()]: <AutoStoriesOutlinedIcon />,
  [DeliveryMethodId.InfographEd.toLowerCase()]: <InsightsIcon />,
  [DeliveryMethodId.ISpring.toLowerCase()]: <SlideshowIcon />,
  [DeliveryMethodId.Page.toLowerCase()]: <CodeIcon />,
  [DeliveryMethodId.Pdf.toLowerCase()]: <PictureAsPdfIcon />,
  [DeliveryMethodId.URL.toLowerCase()]: <LinkIcon />,
  [DeliveryMethodId.Copient.toLowerCase()]: <PsychologyIcon />,
  [DeliveryMethodId.AnatomyTv.toLowerCase()]: <BiotechIcon />,
};

const behaviorByDeliveryMethodId: Record<
  string,
  DeliveryMethodPreviewBehavior
> = {
  [DeliveryMethodId.Inxpo.toLowerCase()]: DeliveryMethodPreviewBehavior.None,
  [DeliveryMethodId.URL.toLowerCase()]: DeliveryMethodPreviewBehavior.NewTab,
  [DeliveryMethodId.Zoom.toLowerCase()]: DeliveryMethodPreviewBehavior.NewTab,
  [DeliveryMethodId.ArcheMedX.toLowerCase()]:
    DeliveryMethodPreviewBehavior.NewTab,
};

export const getDeliveryMethodIcon = (
  deliveryMethodId: string | undefined
): ReactElement =>
  iconsByDeliveryMethodId[deliveryMethodId?.toLowerCase() ?? ""] ?? (
    <ArticleIcon />
  );

export const getDeliveryMethodPreviewBehavior = (
  deliveryMethodId: string | undefined
): DeliveryMethodPreviewBehavior =>
  behaviorByDeliveryMethodId[deliveryMethodId?.toLowerCase() ?? ""] ??
  DeliveryMethodPreviewBehavior.Dialog;

export const isInxpoDeliveryMethod = (
  deliveryMethodId: string | undefined
): boolean =>
  deliveryMethodId?.toLowerCase() === DeliveryMethodId.Inxpo.toLowerCase();

/**
 * KONE-25020. The size an embed is drawn at, read the same way the show key is: out of the settings
 * the delivery method declares. A setting the course never overrode still arrives, carrying the
 * method's default, so one lookup covers both cases.
 */
export const getSettingValue = (
  settingValues: DeliveryMethodSettingValue[] | undefined,
  codeConstant: string
): string | undefined =>
  settingValues
    ?.find((settingValue) => settingValue.setting?.codeConstant === codeConstant)
    ?.value?.trim() || undefined;

export const getShowKey = (
  settingValues: DeliveryMethodSettingValue[] | undefined
): string | undefined =>
  getSettingValue(settingValues, DeliveryMethodSettingCodeConstants.ShowKey);
